// The PMO SELF-ASSESSMENT REPORT — the report system's second template, and
// its first with a CLIENT audience (PMO Self-Assessment MVP,
// PRODUCT_OWNER_DECISION_PMO_SELF_ASSESSMENT_MVP 240926.md §5;
// DESIGN_PMO_SELF_ASSESSMENT_MVP 240926 V1.0.md §4.7; request A5).
//
// 1.1.0 executes the Business Architect's ruling on the instrument
// (RULING_BUSINESS_ARCHITECT_PMO_SELF_ASSESSMENT_C1_C6 240926.md):
//   C2  results are named "self-declared implementation levels";
//   C3  "Unable to assess" and "Not applicable" carry no value, an area with
//       an unassessed question is INCOMPLETE and gets no classification, and
//       the report discloses the coverage every result rests on;
//   C4  the overall result is the mean of the UNROUNDED averages of the
//       fully assessed areas, each counting equally, classified only for
//       display; each area shows its average and its practice responses, and
//       flags any practice answered "Not in place";
//   C5  the three areas are "suggested improvement focus areas", with the
//       ruling's own explanation;
//   C6  the report says that it does not test for critical failures.
//
// Built the way `reportTemplate.ts` is: a versioned template identity and a
// PURE builder. Every per-area number is the kernel's recorded result. Two
// things are derived here, both stated on the report: the overall average
// (C4; the kernel's own verdict averages FLOORED levels, which the ruling
// rejects) and the focus-area selection (C5). Whether that derivation belongs
// in the template is put to the architect (request addendum, A5a).
//
// Inputs beyond the frozen snapshot, both read-only after submission (0008
// refuses edits to a finalized run's responses; 0019 refuses reopening):
// practice titles from the pinned instrument projection, and the
// respondent's own N/A justifications. The snapshot records applicability
// and scores but not text.
//
// THE AUDIENCE IS GATED, NOT ASSUMED. DISCLOSURE_POLICY 240926 V1.1 rule S7:
// `disclosureApproved` is set only in a reviewed change citing the approved
// version of the policy. 1.2.2 is that change: it cites
// DISCLOSURE_POLICY 171026 V1.6 (Schedule A covers template 1.2.x), approved
// by Kenneth as content owner on 2026-10-17. The report still reaches a
// customer only through release block P3.
//
// 1.2.0 executes the System Architect's rulings on the client report
// (ARCHITECT_DECISION_F7_BRANCH_BUILD_AND_PMO_MVP 250926.md; disposition in
// REVIEW_RESPONSE_ARCHITECT_F7_AND_PMO_MVP 250926 V1.0.md §2):
//   A5   one respondent-context line, which names no person (§19.1 G-P5,
//        disclosure rule S6): the builder copies only the identity fields it
//        prints, so a caller's extra fields never reach the model;
//   A5a  the overall derivation carries an identifier, printed on the report;
//   A7   the self-reported statement is pinned by a test;
//   A8   the overall coverage — questions assessed of applicable, summed from
//        the kernel's recorded per-area counts — beside the overall
//        indication, and a partial overall is marked as partial.
//
// NOT A MATURITY LEVEL (M-PMO-1, C2). The kernel's field names say
// "maturity" because the policy was written for maturity instruments. No
// result in this model is ever labelled one; the tests pin that.

import type { EvaluationSnapshot } from "./types";

/**
 * A5a — the ONE derivation of the overall result, by identifier: the mean of
 * the complete areas' UNROUNDED recorded averages, each area counting
 * equally; rounding is presentation only, after calculation. A different
 * derivation is a new identifier (`/ v2`), never a silent edit of this one.
 */
export const PMO_OVERALL_DERIVATION = "overall = mean(unrounded complete-area averages) / v1";

export const PMO_SELF_ASSESSMENT_REPORT_TEMPLATE = {
  id: "pmo-self-assessment-report",
  // 1.1.1: the preview label's stated reason corrected — the disclosure
  // policy covering 1.1.x was approved 2026-09-24 (V1.3); A5 is what remains.
  // 1.2.0: the architect's A5, A5a and A8 change the printed output — the
  // respondent line, the overall coverage figure, the partial marker and the
  // derivation identifier.
  // 1.2.1: the preview label in plain words (no internal ruling names), and
  // a first screen that leads with the result (Kenneth, journey slice 4).
  // 1.2.2: disclosureApproved set, citing DISCLOSURE_POLICY 171026 V1.6; the
  // preview banner is no longer printed. The overall line in "About this
  // assessment" now carries the coverage figure and, when partial, the
  // partial marker (A8; the policy's finding F5).
  // 1.2.3: Harvey balls (Kenneth, 2026-10-28): the overall's and each area's
  // self-declared average drawn as its share of the answer scale, 0-100 %
  // "in place", beside the number and the state, which stay. Presentation
  // only (lib/share.ts); no sentence and no number of the report changes.
  version: "1.2.3",
  audience: "client" as const,
  disclosureGate:
    "Required before this report is shown to a customer: the disclosure policy covering it (DISCLOSURE_POLICY 171026 V1.6, Schedule A for template 1.2.x, approved 2026-10-17 — met) and the System Architect's ruling on request A5 (ruled — met). Release to customers is release block P3.",
  // STRUCTURAL, like the working report's `audience`. While false, every
  // rendering carries `previewLabel`. Set true in 1.2.2, the reviewed change
  // citing DISCLOSURE_POLICY 171026 V1.6 (policy rule S7) — never a screen
  // edit. Setting it back to false restores the banner.
  disclosureApproved: true,
  previewLabel:
    "PREVIEW — this is a test version of the report. It is not yet approved for sharing with customers.",
  // A5 — respondent context. It names no one: no name, email or identifier
  // of the person, whatever the run records.
  respondentStatement: "Completed by one designated respondent from the customer organisation.",
  // A8 — printed beside an overall indication that rests on only some areas.
  partialLabel:
    "Partial — this overall indication rests on only some of the applicable practice areas; it is not the result of a complete assessment.",
  overallDerivation: PMO_OVERALL_DERIVATION,
  methodStatement:
    "This is a customer self-assessment. Every result in this report comes from the answers given by the person who completed it.",
  levelsStatement:
    "The results are self-declared implementation levels: they describe how far the respondent reports each practice to be in place. They are not verified maturity levels, and \"Fully in place\" here is not equivalent to any level of a LINNFOSS maturity assessment.",
  coverageStatement:
    "\"Unable to assess\" and \"Not applicable\" carry no value and are never counted as \"Not in place\". A practice area with any question that could not be assessed is reported as incomplete and is not classified. The overall indication is the average of the unrounded results of the fully assessed practice areas, each counting equally.",
  focusHeading: "Suggested improvement focus areas — based on assessment results",
  focusExplanation:
    "These areas have been identified from their assessment results. Their business importance, urgency and implementation sequence should be confirmed against the organisation's objectives, delivery risks and available capacity.",
  focusRule:
    "The focus areas are the fully assessed practice areas with the lowest average answer. Where areas are equal, the area with more practices answered \"Not in place\" comes first, then the area that appears first in the questionnaire. Areas already fully in place, incomplete or not applicable are not proposed.",
  limitationsStatement:
    "The results are self-reported and have not been independently validated. No evidence was reviewed and no assessor took part. The results indicate where a closer look is likely to be useful; they are not a maturity rating, a certification or a benchmark, and they do not establish the organisation's strategic priorities, target operating model or investment decisions.",
  criticalStatement:
    "This self-assessment does not test for critical governance failures. That none is reported here does not mean that none exists.",
} as const;

export const UNABLE_TO_ASSESS = "Unable to assess";
export const NOT_APPLICABLE = "Not applicable";

export interface ScaleLevel {
  value: number;
  state: string;
  anchor: string;
}

export interface PmoReportIdentity {
  customer: string;
  assessedOn: string;          // ISO timestamp of the evaluation
  instrumentTitle: string;
  instrumentVersion: string;
  definitionHash: string;
}

/** A practice's title, from the pinned instrument projection. */
export interface PracticeTitle {
  blockId: string;
  criterionId: string;
  sequence: number;
  title: string;
}

/** The respondent's own N/A justifications, keyed by block (and criterion). */
export interface Justifications {
  areas: ReadonlyMap<string, string>;               // blockId → text
  practices: ReadonlyMap<string, string>;           // `${blockId}\u0000${criterionId}` → text
}

export interface PmoPractice {
  criterionId: string;
  title: string;
  response: string;            // a scale label, UNABLE_TO_ASSESS or NOT_APPLICABLE
  score: number | null;        // the answer on the scale — ONLY when assessed (1.2.3, the practice balls)
  notInPlace: boolean;
  justification: string | null;
}

export type AreaStatus = "complete" | "incomplete" | "not_applicable";

export interface PmoAreaResult {
  blockId: string;
  sequence: number;
  title: string;
  status: AreaStatus;
  state: string | null;        // classification — ONLY when complete
  average: number | null;      // unrounded mean of the answers — ONLY when complete
  assessed: number;            // questions answered on the scale
  applicable: number;          // questions that apply (N/A excluded)
  notInPlace: number;
  justification: string | null; // when the whole area is not applicable
  practices: PmoPractice[];
}

export interface PmoOpportunity {
  blockId: string;
  areaTitle: string;
  perspective: string;
  answer: string;
  score: number;               // the answer on the scale (1.2.3, the practice balls)
  text: string;
  reason: string | null;       // the respondent's own reason for the answer, if given
}

export interface PmoFocusArea {
  rank: number;
  blockId: string;
  title: string;
  state: string;
  opportunities: PmoOpportunity[];
}

export interface PmoOverall {
  average: number | null;      // mean of the complete areas' unrounded averages
  state: string | null;
  areasComplete: number;
  areasIncomplete: number;
  areasNotApplicable: number;
  isComplete: boolean;         // no applicable area is incomplete
  // A8 — summed from the recorded per-area counts, whole-area N/A excluded.
  questionsAssessed: number;   // answered on the scale
  questionsApplicable: number; // that apply
  coverageText: string;        // e.g. "38 of 42 questions assessed"
  isPartial: boolean;          // an overall exists, resting on fewer than all applicable areas
  derivation: string;          // A5a — PMO_OVERALL_DERIVATION
}

export interface PmoReportModel {
  template: typeof PMO_SELF_ASSESSMENT_REPORT_TEMPLATE;
  identity: PmoReportIdentity;
  overall: PmoOverall;
  summary: string;
  distribution: { state: string; areas: number }[];
  areas: PmoAreaResult[];
  opportunities: PmoOpportunity[];
  focusAreas: PmoFocusArea[];
  focusNote: string | null;
}

export class PmoReportError extends Error {}

const key = (b: string, c: string) => `${b}\u0000${c}`;

function labelFor(levels: readonly ScaleLevel[], value: number): string {
  const hit = levels.find((l) => l.value === value);
  if (!hit) throw new PmoReportError(`the scale has no level ${value}`);
  return hit.state;
}

/**
 * The focus areas — rule C5, printed on the report.
 *
 * Only COMPLETE areas are candidates: an incomplete area's average rests on
 * fewer answers than the others', so ranking it beside them would compare
 * unlike things. Lowest average first; ties broken by more answers at the
 * scale minimum, then by questionnaire order. An area already at the scale
 * maximum is never proposed. Fewer than three candidates → fewer proposed,
 * and the report says so.
 */
export function selectFocusAreas(
  areas: readonly PmoAreaResult[],
  levelOf: (a: PmoAreaResult) => number,
  scaleMax: number,
): PmoAreaResult[] {
  return areas
    .filter((a) => a.status === "complete" && a.average !== null && levelOf(a) < scaleMax)
    .slice()
    .sort((a, b) => a.average! - b.average! || b.notInPlace - a.notInPlace || a.sequence - b.sequence)
    .slice(0, 3);
}

export function buildPmoReportModel(input: {
  snapshot: EvaluationSnapshot;
  levels: readonly ScaleLevel[];
  identity: PmoReportIdentity;
  practices: readonly PracticeTitle[];
  justifications: Justifications;
}): PmoReportModel {
  const { snapshot, levels, identity, practices, justifications } = input;
  const result = snapshot.result;

  // A report is built only from a result the database would have let the
  // run finalize on — never a partial report that looks complete.
  if (!result.validation.ok) throw new PmoReportError("the evaluation did not validate");
  if (!result.formal_verdict) {
    throw new PmoReportError(`no result was issued: ${result.formal_verdict_withheld ?? "withheld"}`);
  }
  if (levels.length === 0) throw new PmoReportError("the instrument's scale is missing");

  const min = Math.min(...levels.map((l) => l.value));
  const max = Math.max(...levels.map((l) => l.value));
  const titleOf = new Map(practices.map((p) => [key(p.blockId, p.criterionId), p]));

  const levelById = new Map<string, number>();
  const areas: PmoAreaResult[] = result.blocks
    .slice()
    .sort((a, b) => a.sequence - b.sequence)
    .map((b) => {
      const areaNA = b.applicability === "not_applicable";
      const practiceRows: PmoPractice[] = b.criteria
        .slice()
        .sort(
          (x, y) =>
            (titleOf.get(key(b.block_id, x.criterion_id))?.sequence ?? 0) -
            (titleOf.get(key(b.block_id, y.criterion_id))?.sequence ?? 0),
        )
        .map((c) => {
          const title = titleOf.get(key(b.block_id, c.criterion_id))?.title ?? c.criterion_id;
          if (areaNA || c.applicability === "not_applicable") {
            return {
              criterionId: c.criterion_id,
              title,
              response: NOT_APPLICABLE,
              score: null,
              notInPlace: false,
              justification: areaNA ? null : (justifications.practices.get(key(b.block_id, c.criterion_id)) ?? null),
            };
          }
          if (c.contributes_to_mean && c.score !== null) {
            return {
              criterionId: c.criterion_id,
              title,
              response: labelFor(levels, c.score),
              score: c.score,
              notInPlace: c.score === min,
              justification: justifications.practices.get(`${b.block_id}\u0000${c.criterion_id}`) ?? null,
            };
          }
          return { criterionId: c.criterion_id, title, response: UNABLE_TO_ASSESS, score: null, notInPlace: false, justification: null };
        });

      const assessed = b.coverage.covered;
      const applicable = b.coverage.applicable;
      const status: AreaStatus = areaNA || applicable === 0
        ? "not_applicable"
        : assessed < applicable
          ? "incomplete"
          : "complete";
      const notInPlace = practiceRows.filter((p) => p.notInPlace).length;

      let state: string | null = null;
      let average: number | null = null;
      if (status === "complete") {
        const level = b.calculated_maturity_level;
        if (level === null || b.calculated_raw_score === null) {
          throw new PmoReportError(`practice area '${b.block_id}' is fully assessed but has no result`);
        }
        levelById.set(b.block_id, level);
        state = labelFor(levels, level);        // capped by the absence guard (C4)
        average = b.calculated_raw_score;       // unrounded (C4)
      }

      return {
        blockId: b.block_id,
        sequence: b.sequence,
        title: b.title,
        status,
        state,
        average,
        assessed,
        applicable,
        notInPlace,
        justification: areaNA ? (justifications.areas.get(b.block_id) ?? null) : null,
        practices: practiceRows,
      };
    });

  const areaOf = new Map(areas.map((a) => [a.blockId, a]));

  const opportunities: PmoOpportunity[] = result.recommendations
    .slice()
    .sort((a, b) => (areaOf.get(a.block_id)?.sequence ?? 0) - (areaOf.get(b.block_id)?.sequence ?? 0))
    .map((r) => ({
      blockId: r.block_id,
      areaTitle: areaOf.get(r.block_id)?.title ?? r.block_id,
      perspective: r.perspective,
      answer: labelFor(levels, r.score),
      score: r.score,
      text: r.text,
      reason: justifications.practices.get(`${r.block_id}\u0000${r.criterion_id}`) ?? null,
    }));

  // C4 — the overall result from UNROUNDED area averages, each area equal.
  const complete = areas.filter((a) => a.status === "complete");
  const average =
    complete.length > 0 ? complete.reduce((n, a) => n + a.average!, 0) / complete.length : null;
  // A8 — the overall coverage, reconstructed from the kernel's recorded
  // per-area counts; a whole area marked not applicable has nothing to count.
  const counted = areas.filter((a) => a.status !== "not_applicable");
  const questionsAssessed = counted.reduce((n, a) => n + a.assessed, 0);
  const questionsApplicable = counted.reduce((n, a) => n + a.applicable, 0);
  const isComplete = areas.every((a) => a.status !== "incomplete");
  const overall: PmoOverall = {
    average,
    state: average === null ? null : labelFor(levels, Math.max(min, Math.min(max, Math.floor(average + 1e-9)))),
    areasComplete: complete.length,
    areasIncomplete: areas.filter((a) => a.status === "incomplete").length,
    areasNotApplicable: areas.filter((a) => a.status === "not_applicable").length,
    isComplete,
    questionsAssessed,
    questionsApplicable,
    coverageText: `${questionsAssessed} of ${questionsApplicable} ${questionsApplicable === 1 ? "question" : "questions"} assessed`,
    isPartial: average !== null && !isComplete,
    derivation: PMO_OVERALL_DERIVATION,
  };

  const chosen = selectFocusAreas(areas, (a) => levelById.get(a.blockId)!, max);
  const focusAreas: PmoFocusArea[] = chosen.map((a, i) => ({
    rank: i + 1,
    blockId: a.blockId,
    title: a.title,
    state: a.state!,
    opportunities: opportunities.filter((o) => o.blockId === a.blockId),
  }));

  const distribution = levels
    .slice()
    .sort((a, b) => b.value - a.value)
    .map((l) => ({ state: l.state, areas: complete.filter((a) => a.state === l.state).length }));

  return {
    template: PMO_SELF_ASSESSMENT_REPORT_TEMPLATE,
    // A5 — the printed fields only: nothing else a caller passes (a
    // respondent's name or email, say) can reach the model.
    identity: {
      customer: identity.customer,
      assessedOn: identity.assessedOn,
      instrumentTitle: identity.instrumentTitle,
      instrumentVersion: identity.instrumentVersion,
      definitionHash: identity.definitionHash,
    },
    overall,
    summary: summarise(areas.length, overall, distribution, focusAreas),
    distribution,
    areas,
    opportunities,
    focusAreas,
    focusNote:
      focusAreas.length < 3
        ? focusAreas.length === 0
          ? "No fully assessed practice area is below fully in place, so no focus areas are suggested from these answers."
          : `Only ${focusAreas.length} fully assessed practice area${focusAreas.length === 1 ? " is" : "s are"} below fully in place, so only ${focusAreas.length === 1 ? "one focus area is" : `${focusAreas.length} focus areas are`} suggested.`
        : null,
  };
}

function summarise(
  total: number,
  overall: PmoOverall,
  distribution: { state: string; areas: number }[],
  focus: PmoFocusArea[],
): string {
  if (overall.state === null) {
    return "No practice area could be fully assessed, so no overall indication is given.";
  }
  const basis =
    overall.areasComplete === total
      ? `all ${total} practice areas`
      : `${overall.areasComplete} of the ${total} practice areas`;
  const gaps: string[] = [];
  if (overall.areasIncomplete > 0) {
    gaps.push(`${overall.areasIncomplete} ${overall.areasIncomplete === 1 ? "area is" : "areas are"} incomplete because some questions could not be assessed`);
  }
  if (overall.areasNotApplicable > 0) {
    gaps.push(`${overall.areasNotApplicable} ${overall.areasNotApplicable === 1 ? "area was" : "areas were"} marked not applicable`);
  }
  const parts = distribution.filter((d) => d.areas > 0).map((d) => `${d.areas} ${d.state.toLowerCase()}`);
  const spread = parts.length > 0 ? ` Of the fully assessed areas, the answers describe ${joinList(parts)}.` : "";
  const gapText = gaps.length > 0 ? ` ${capitalise(joinList(gaps))}.` : "";
  const focusText =
    focus.length > 0 ? ` The results point first to ${joinList(focus.map((f) => f.title.toLowerCase()))}.` : "";
  return `The answers give an overall indication of "${overall.state}", based on ${basis}.${gapText}${spread}${focusText}`;
}

function capitalise(s: string): string {
  return s.length > 0 ? s[0]!.toUpperCase() + s.slice(1) : s;
}

function joinList(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}
